/**
 * NatalChartSVG — интерактивная SVG-визуализация натальной карты
 *
 * Отображает:
 * - 12 знаков зодиака (внешний круг)
 * - 12 домов (средний круг с линиями-разделителями)
 * - Планеты с символами (внутренний круг)
 * - Аспекты (линии между планетами)
 * - Hover-эффекты на планетах
 */

import React, { useState, useMemo } from 'react';

// ============================================================
// Типы
// ============================================================

interface PlanetData {
  name: string;
  nameRu: string;
  symbol: string;
  sign: string;
  signRu: string;
  longitude: number;
  house: number;
  retrograde: boolean;
}

interface HouseData {
  number: number;
  sign: string;
  signRu: string;
  longitude: number;
}

interface AspectData {
  planet1: string;
  planet2: string;
  type: string;
  orb: number;
}

interface NatalChartSVGProps {
  planets: PlanetData[];
  houses: HouseData[];
  aspects: AspectData[];
  ascendantLongitude?: number;
  size?: number;
}

// ============================================================
// Константы
// ============================================================

const ZODIAC_SYMBOLS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];

/** Цвета аспектов */
const ASPECT_COLORS: Record<string, string> = {
  'Соединение': '#FFD700',
  'conjunction': '#FFD700',
  'Оппозиция': '#FF4444',
  'opposition': '#FF4444',
  'Трин': '#44BB44',
  'trine': '#44BB44',
  'Квадрат': '#FF6600',
  'square': '#FF6600',
  'Секстиль': '#4488FF',
  'sextile': '#4488FF',
  'Квинконс': '#AA44FF',
  'quincunx': '#AA44FF',
};

/** Радиусы колец (в единицах viewBox 0-600) */
const RADIUS = {
  outer: 280,    // внешний край знаков
  signInner: 245, // внутренний край знаков
  houseOuter: 245, // внешний край домов
  houseInner: 180, // внутренний край домов
  planet: 155,     // кольцо планет
  inner: 120,      // внутренний круг
  center: 30,      // центр
};

// ============================================================
// Компонент
// ============================================================

const NatalChartSVG: React.FC<NatalChartSVGProps> = ({
  planets,
  houses,
  aspects,
  ascendantLongitude = 0,
  size = 600,
}) => {
  const [hoveredPlanet, setHoveredPlanet] = useState<string | null>(null);
  const [selectedPlanet, setSelectedPlanet] = useState<string | null>(null);

  const cx = 300;
  const cy = 300;

  /** Конвертация эклиптической долготы в SVG-угол */
  const lonToAngle = (longitude: number): number => {
    // Асцендент слева (180°), знаки против часовой стрелки
    return (ascendantLongitude - longitude + 180) * (Math.PI / 180);
  };

  /** Координаты точки на круге по долготе */
  const pointOnCircle = (longitude: number, radius: number): { x: number; y: number } => {
    const angle = lonToAngle(longitude);
    return {
      x: cx + radius * Math.cos(angle),
      y: cy - radius * Math.sin(angle),
    };
  };

  /** Позиции планет (с предотвращением наложений) */
  const planetPositions = useMemo(() => {
    const sorted = [...planets].sort((a, b) => a.longitude - b.longitude);
    const positions: Map<string, { x: number; y: number; angle: number }> = new Map();
    const minAngleGap = 8 * (Math.PI / 180); // минимум 8° между планетами

    sorted.forEach((planet, idx) => {
      let adjustedLon = planet.longitude;

      // Проверяем наложение с предыдущей планетой
      if (idx > 0) {
        const prev = sorted[idx - 1];
        const prevPos = positions.get(prev.name);
        if (prevPos) {
          const prevAngle = lonToAngle(prev.longitude);
          let currentAngle = lonToAngle(adjustedLon);
          let diff = prevAngle - currentAngle;
          if (diff < 0) diff += 2 * Math.PI;
          if (diff < minAngleGap) {
            // Сдвигаем текущую планету
            const newAngle = prevAngle - minAngleGap;
            adjustedLon = ascendantLongitude + 180 - newAngle * (180 / Math.PI);
          }
        }
      }

      const pos = pointOnCircle(adjustedLon, RADIUS.planet);
      positions.set(planet.name, {
        x: pos.x,
        y: pos.y,
        angle: lonToAngle(adjustedLon),
      });
    });

    return positions;
  }, [planets, ascendantLongitude]);

  /** Активные аспекты для выбранной планеты */
  const highlightedAspects = useMemo(() => {
    if (!selectedPlanet) return [];
    return aspects.filter(
      a => a.planet1 === selectedPlanet || a.planet2 === selectedPlanet
    );
  }, [selectedPlanet, aspects]);

  const activePlanet = hoveredPlanet || selectedPlanet;
  const activePlanetData = planets.find(p => p.name === activePlanet);

  return (
    <div style={{ position: 'relative', width: size, maxWidth: '100%', margin: '0 auto' }}>
      <svg
        viewBox="0 0 600 600"
        width={size}
        height={size}
        style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
      >
        {/* Определения градиентов */}
        <defs>
          <radialGradient id="bgGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1a1a2e" />
            <stop offset="100%" stopColor="#0f0f1a" />
          </radialGradient>
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#d4af37" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Фон */}
        <circle cx={cx} cy={cy} r={295} fill="url(#bgGradient)" />

        {/* Внешний круг (граница знаков) */}
        <circle cx={cx} cy={cy} r={RADIUS.outer} fill="none" stroke="#d4af37" strokeWidth="2" opacity="0.6" />
        <circle cx={cx} cy={cy} r={RADIUS.signInner} fill="none" stroke="#d4af37" strokeWidth="1.5" opacity="0.4" />

        {/* Знаки зодиака */}
        {ZODIAC_SYMBOLS.map((symbol, i) => {
          const midLon = i * 30 + 15; // середина знака
          const pos = pointOnCircle(midLon, (RADIUS.outer + RADIUS.signInner) / 2);
          return (
            <text
              key={`sign-${i}`}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#d4af37"
              fontSize="22"
              opacity="0.85"
              style={{ fontFamily: 'serif' }}
            >
              {symbol}
            </text>
          );
        })}

        {/* Линии-разделители знаков */}
        {Array.from({ length: 12 }).map((_, i) => {
          const lon = i * 30;
          const inner = pointOnCircle(lon, RADIUS.signInner);
          const outer = pointOnCircle(lon, RADIUS.outer);
          return (
            <line
              key={`sign-line-${i}`}
              x1={inner.x} y1={inner.y}
              x2={outer.x} y2={outer.y}
              stroke="#d4af37"
              strokeWidth="1"
              opacity="0.5"
            />
          );
        })}

        {/* Круг домов */}
        <circle cx={cx} cy={cy} r={RADIUS.houseOuter} fill="none" stroke="#d4af37" strokeWidth="1.5" opacity="0.3" />
        <circle cx={cx} cy={cy} r={RADIUS.houseInner} fill="none" stroke="#d4af37" strokeWidth="1.5" opacity="0.4" />

        {/* Линии домов */}
        {houses.map((house) => {
          const inner = pointOnCircle(house.longitude, RADIUS.houseInner);
          const outer = pointOnCircle(house.longitude, RADIUS.houseOuter);
          const isAngular = [1, 4, 7, 10].includes(house.number);
          return (
            <line
              key={`house-${house.number}`}
              x1={inner.x} y1={inner.y}
              x2={outer.x} y2={outer.y}
              stroke={isAngular ? '#d4af37' : '#8888aa'}
              strokeWidth={isAngular ? 2 : 1}
              opacity={isAngular ? 0.8 : 0.4}
            />
          );
        })}

        {/* Номера домов */}
        {houses.map((house) => {
          const nextHouse = houses[house.number % 12];
          const midLon = (house.longitude + nextHouse.longitude) / 2;
          // Простая корректировка для перехода через 0°
          const adjustedMidLon = Math.abs(house.longitude - nextHouse.longitude) > 180
            ? normalizeDeg((house.longitude + nextHouse.longitude + 360) / 2)
            : midLon;
          const pos = pointOnCircle(adjustedMidLon, (RADIUS.houseOuter + RADIUS.houseInner) / 2);
          return (
            <text
              key={`house-num-${house.number}`}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#8888aa"
              fontSize="11"
              opacity="0.6"
            >
              {house.number}
            </text>
          );
        })}

        {/* Внутренний круг */}
        <circle cx={cx} cy={cy} r={RADIUS.inner} fill="none" stroke="#d4af37" strokeWidth="1" opacity="0.2" />

        {/* Аспекты (линии между планетами) */}
        {aspects.map((aspect, idx) => {
          const pos1 = planetPositions.get(aspect.planet1);
          const pos2 = planetPositions.get(aspect.planet2);
          if (!pos1 || !pos2) return null;

          const color = ASPECT_COLORS[aspect.type] || '#666666';
          const isHighlighted = highlightedAspects.includes(aspect);
          const opacity = activePlanet
            ? (isHighlighted ? 0.8 : 0.1)
            : 0.35;

          return (
            <line
              key={`aspect-${idx}`}
              x1={pos1.x} y1={pos1.y}
              x2={pos2.x} y2={pos2.y}
              stroke={color}
              strokeWidth={isHighlighted ? 2 : 1}
              opacity={opacity}
              strokeDasharray={aspect.type === 'Квадрат' || aspect.type === 'square' ? '4,2' : undefined}
            />
          );
        })}

        {/* Планеты */}
        {planets.map((planet) => {
          const pos = planetPositions.get(planet.name);
          if (!pos) return null;

          const isActive = activePlanet === planet.name;
          const isRetrograde = planet.retrograde;

          return (
            <g
              key={`planet-${planet.name}`}
              onMouseEnter={() => setHoveredPlanet(planet.name)}
              onMouseLeave={() => setHoveredPlanet(null)}
              onClick={() => setSelectedPlanet(
                selectedPlanet === planet.name ? null : planet.name
              )}
              style={{ cursor: 'pointer' }}
            >
              {/* Подсветка при hover */}
              {isActive && (
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r="16"
                  fill="#d4af37"
                  opacity="0.2"
                  filter="url(#glow)"
                />
              )}

              {/* Символ планеты */}
              <text
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={isActive ? '#FFD700' : '#d4af37'}
                fontSize={isActive ? '20' : '16'}
                fontWeight={isActive ? 'bold' : 'normal'}
                opacity={isActive ? 1 : 0.85}
                style={{
                  fontFamily: 'serif',
                  transition: 'all 0.2s ease',
                }}
              >
                {planet.symbol}
              </text>

              {/* Маркер ретроградности */}
              {isRetrograde && (
                <text
                  x={pos.x + 10}
                  y={pos.y - 8}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#FF6666"
                  fontSize="8"
                  fontWeight="bold"
                >
                  R
                </text>
              )}
            </g>
          );
        })}

        {/* Центр */}
        <circle cx={cx} cy={cy} r={RADIUS.center} fill="url(#centerGlow)" />
        <circle cx={cx} cy={cy} r="4" fill="#d4af37" opacity="0.8" />

        {/* Маркеры угловых точек (ASC, MC, DSC, IC) */}
        {[
          { label: 'ASC', longitude: ascendantLongitude, color: '#FFD700' },
          { label: 'MC', longitude: ascendantLongitude + 270, color: '#FFD700' },
          { label: 'DSC', longitude: ascendantLongitude + 180, color: '#FFD700' },
          { label: 'IC', longitude: ascendantLongitude + 90, color: '#FFD700' },
        ].map((point) => {
          const pos = pointOnCircle(point.longitude, RADIUS.outer + 12);
          return (
            <text
              key={point.label}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={point.color}
              fontSize="10"
              fontWeight="bold"
              opacity="0.7"
            >
              {point.label}
            </text>
          );
        })}
      </svg>

      {/* Тултип при наведении */}
      {activePlanetData && (
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(15, 15, 26, 0.95)',
            border: '1px solid #d4af37',
            borderRadius: 8,
            padding: '8px 16px',
            color: '#d4af37',
            fontSize: 14,
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <strong>{activePlanetData.symbol} {activePlanetData.nameRu}</strong>
          {' — '}
          {activePlanetData.signRu}, {activePlanetData.house} дом
          {activePlanetData.retrograde && ' (ретроградная)'}
        </div>
      )}
    </div>
  );
};

/** Нормализация угла */
function normalizeDeg(deg: number): number {
  deg = deg % 360;
  return deg < 0 ? deg + 360 : deg;
}

export default NatalChartSVG;
